import type { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useLayoutEffect, useMemo, useRef } from "react";

import { SimulatorPickActionCell } from "@/components/Reports/SimulatorPickActionCell";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import type { ManagementExplorerSliceRow } from "../../../../types/managementExplorer";
import {
  EXIT_PLACEMENT_VOL_GLOSSARY,
  exitPlacementSliceDisplayLabel,
  exitPlacementStopTargetTooltip,
  formatPriceDistanceFromEntry,
  formatRewardRisk,
  sliceCanAddToCart,
  sliceExitDisplay,
} from "../../../../utils/exitPlacementDisplay";
import { buildExplorerSliceId } from "../../../../utils/managementExplorer";
import {
  marketContextActiveRowSelector,
  scheduleScrollMarketContextRow,
} from "../../../../utils/marketContextEvidenceScroll";
import { breakdownMapToSliceKind } from "../marketContextSliceDisplay";
import { ExitPlacementCategoryBadge } from "./ExitPlacementCategoryBadge";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";
import { TradeLogSortableCell } from "../../TradeLog/components/tradeLogSortableCell";
import {
  AddToSimulatorColumnHeader,
  fmtInt,
  fmtNum,
  fmtPct,
  fmtPnl,
  MarketContextActionColumnCell,
  MarketContextPreviewTradesButton,
  MarketContextPreviewTradesColumnHeader,
  MarketContextMetricColumnHeader,
  MarketContextTooltipContent,
} from "./marketContextDataTableShared";
import { MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT } from "./MarketContextPivotTable";
import { WIN_RATE_COLUMN_TOOLTIP } from "./marketContextTableTooltips";

/** Rows below this sample size are treated as low-confidence / directional only. */
const EXIT_PLACEMENT_LOW_SAMPLE_THRESHOLD = 10;

function isLowConfidenceSample(sample: number | null | undefined): boolean {
  return typeof sample === "number" && Number.isFinite(sample) && sample < EXIT_PLACEMENT_LOW_SAMPLE_THRESHOLD;
}

export type ExitPlacementExplorerTableProps = {
  rows: ManagementExplorerSliceRow[];
  pickedSliceIds: Set<string>;
  onTogglePick: (row: ManagementExplorerSliceRow) => void;
  onOpenDetails: (row: ManagementExplorerSliceRow) => void;
  onPreviewSlice?: (row: ManagementExplorerSliceRow) => void;
  previewActiveKey?: string | readonly string[];
  previewLoadingKey?: string | readonly string[];
  activeRowId?: string;
  evidencePulseNonce?: number;
  emptyMessage?: string;
  /** Report candle TF — scales long/short direction bar-window labels. */
  candleTimeframe?: string | null;
};

function SlTpHeader(props: { label: string }): React.ReactElement {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DataTableButtonPlaceholder title={props.label} />
        </TooltipTrigger>
        <TooltipContent
          side="top"
          arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
          className={metricTooltipShellClassName("max-w-xs")}
        >
          <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
            {EXIT_PLACEMENT_VOL_GLOSSARY}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function DataTableButtonPlaceholder({ title }: { title: string }): React.ReactElement {
  return (
    <span className="cursor-help border-b border-dotted border-current text-sm font-medium text-[color:var(--brand-tropical-surf)]">
      {title}
    </span>
  );
}

function createColumns(props: ExitPlacementExplorerTableProps): ColumnDef<ManagementExplorerSliceRow>[] {
  const cols: ColumnDef<ManagementExplorerSliceRow>[] = [
    {
      id: "context",
      accessorFn: (row) => exitPlacementSliceDisplayLabel(row),
      header: ({ column }) => (
        <DataTableColumnHeader<ManagementExplorerSliceRow, unknown> column={column} title="Context" />
      ),
      cell: ({ row }) => {
        const label = exitPlacementSliceDisplayLabel(row.original);
        return (
          <TradeLogSortableCell
            data-market-context-wrap-cell=""
            className="min-w-[16rem] whitespace-normal break-words font-medium"
          >
            {label || "—"}
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "category",
      accessorFn: (row) => breakdownMapToSliceKind(String(row.breakdown_map ?? "")),
      header: ({ column }) => (
        <DataTableColumnHeader<ManagementExplorerSliceRow, unknown> column={column} title="Category" />
      ),
      cell: ({ row }) => {
        const breakdownMap = String(row.original.breakdown_map ?? "").trim();
        return (
          <ExitPlacementCategoryBadge
            breakdownMap={breakdownMap}
            candleTimeframe={props.candleTimeframe}
          />
        );
      },
    },
    {
      id: "pick",
      enableSorting: false,
      header: () => <AddToSimulatorColumnHeader />,
      cell: ({ row }) => {
        const id = buildExplorerSliceId(row.original);
        const canPick = sliceCanAddToCart(row.original) && Boolean(id);
        if (!canPick) {
          return (
            <div data-market-context-simulator-pick-cell="" className="flex items-center py-1">
              <span className="text-sm text-muted-foreground">—</span>
            </div>
          );
        }
        const picked = Boolean(id && props.pickedSliceIds.has(id));
        return (
          <div
            data-market-context-simulator-pick-cell=""
            className="flex items-center py-1"
          >
            <SimulatorPickActionCell
              picked={picked}
              addAriaLabel="Add to simulator picks"
              removeAriaLabel="Remove from simulator picks"
              onAdd={() => props.onTogglePick(row.original)}
              onRemove={picked ? () => props.onTogglePick(row.original) : undefined}
            />
          </div>
        );
      },
    },
    {
      id: "sample",
      accessorFn: (row) => row.sample_size ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Sample"
          tooltip={`All trades in this row, including scratch/breakeven. Sample < ${EXIT_PLACEMENT_LOW_SAMPLE_THRESHOLD} is marked low confidence (directional only).`}
        />
      ),
      cell: ({ row }) => {
        const sample = row.original.sample_size;
        const low = isLowConfidenceSample(sample);
        const formatted = fmtInt(sample);
        if (!low) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              {formatted}
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-exit-placement-low-sample=""
                    className="cursor-help border-0 bg-transparent p-0 font-medium tabular-nums underline decoration-current decoration-dotted underline-offset-2"
                    style={{ color: "var(--brand-comparison-period)" }}
                    aria-label={`Low confidence: sample ${formatted} is below ${EXIT_PLACEMENT_LOW_SAMPLE_THRESHOLD}`}
                  >
                    {formatted}
                  </button>
                </TooltipTrigger>
                <MarketContextTooltipContent widthClass="max-w-[280px]">
                  {`Low confidence: sample ${formatted} is below ${EXIT_PLACEMENT_LOW_SAMPLE_THRESHOLD}. Treat metrics as directional only.`}
                </MarketContextTooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "winRate",
      accessorFn: (row) => row.win_rate ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader column={column} title="Win rate" tooltip={WIN_RATE_COLUMN_TOOLTIP} />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtPct(row.original.win_rate)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "expectancy",
      accessorFn: (row) => row.expectancy ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Expectancy"
          tooltip="Average expected return per trade for the slice."
        />
      ),
      cell: ({ row }) => (
        <TradeLogSortableCell align="right" className="font-medium tabular-nums">
          {fmtNum(row.original.expectancy)}
        </TradeLogSortableCell>
      ),
    },
    {
      id: "totalPnl",
      accessorFn: (row) => row.total_pnl ?? Number.NEGATIVE_INFINITY,
      header: ({ column }) => (
        <MarketContextMetricColumnHeader
          column={column}
          title="Total PnL"
          tooltip="Total profit and loss across all trades in the slice."
        />
      ),
      cell: ({ row }) => {
        const totalPnl = row.original.total_pnl;
        if (totalPnl == null || !Number.isFinite(totalPnl)) {
          return (
            <TradeLogSortableCell align="right" className="font-medium tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            <span data-trade-log-pnl="" style={{ color: pnlBarFill(totalPnl) }}>
              {fmtPnl(totalPnl)}
            </span>
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "stop",
      enableSorting: false,
      header: () => <SlTpHeader label="Stop" />,
      cell: ({ row }) => {
        const display = sliceExitDisplay(row.original);
        const primary = formatPriceDistanceFromEntry(display?.sl_distance_price, display?.symbol);
        const tooltip = exitPlacementStopTargetTooltip(row.original);
        if (primary === "—") {
          return (
            <TradeLogSortableCell align="right" className="tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            {tooltip ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted underline-offset-2">
                      {primary}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                    className={metricTooltipShellClassName("max-w-xs")}
                  >
                    <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                      {tooltip}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              primary
            )}
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "target",
      enableSorting: false,
      header: () => <SlTpHeader label="Target" />,
      cell: ({ row }) => {
        const display = sliceExitDisplay(row.original);
        const primary = formatPriceDistanceFromEntry(display?.tp_distance_price, display?.symbol);
        const tooltip = exitPlacementStopTargetTooltip(row.original);
        if (primary === "—") {
          return (
            <TradeLogSortableCell align="right" className="tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            {tooltip ? (
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="cursor-help underline decoration-dotted underline-offset-2">
                      {primary}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                    className={metricTooltipShellClassName("max-w-xs")}
                  >
                    <p className={`text-[11px] leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                      {tooltip}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              primary
            )}
          </TradeLogSortableCell>
        );
      },
    },
    {
      id: "rewardRisk",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader<ManagementExplorerSliceRow, unknown>
          column={column}
          title="R:R"
          align="right"
        />
      ),
      cell: ({ row }) => {
        const display = sliceExitDisplay(row.original);
        const hasBoth =
          display?.sl_distance_price != null && display?.tp_distance_price != null;
        if (!hasBoth) {
          return (
            <TradeLogSortableCell align="right" className="tabular-nums">
              —
            </TradeLogSortableCell>
          );
        }
        return (
          <TradeLogSortableCell align="right" className="font-medium tabular-nums">
            {formatRewardRisk(row.original.exit_placement?.engine)}
          </TradeLogSortableCell>
        );
      },
    },
  ];

  cols.push({
    id: "details",
    enableSorting: false,
    header: () => (
      <div className="flex h-8 items-center font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]">
        Details
      </div>
    ),
    cell: ({ row }) => {
      const id = buildExplorerSliceId(row.original);
      return (
        <MarketContextActionColumnCell>
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-4 active:translate-y-0"
            disabled={!id}
            onClick={() => props.onOpenDetails(row.original)}
          >
            <span className="normal-case tracking-normal">Details</span>
          </Button>
        </MarketContextActionColumnCell>
      );
    },
  });

  if (props.onPreviewSlice) {
    cols.push({
      id: "previewTrades",
      enableSorting: false,
      header: () => <MarketContextPreviewTradesColumnHeader />,
      cell: ({ row }) => {
        const id = buildExplorerSliceId(row.original);
        const rowKey = id ? `slice:${id}` : "";
        const disabled =
          !id || (typeof row.original.sample_size === "number" && row.original.sample_size <= 0);
        return (
          <MarketContextPreviewTradesButton
            rowKey={rowKey}
            activeKey={props.previewActiveKey}
            loadingKey={props.previewLoadingKey}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              props.onPreviewSlice?.(row.original);
            }}
          />
        );
      },
    });
  }

  return cols;
}

export function ExitPlacementExplorerTable(props: ExitPlacementExplorerTableProps): React.ReactElement {
  const lastHandledEvidencePulseRef = useRef(0);
  const onOpenDetailsRef = useRef(props.onOpenDetails);
  const onPreviewSliceRef = useRef(props.onPreviewSlice);
  const onTogglePickRef = useRef(props.onTogglePick);
  onOpenDetailsRef.current = props.onOpenDetails;
  onPreviewSliceRef.current = props.onPreviewSlice;
  onTogglePickRef.current = props.onTogglePick;

  const columns = useMemo(
    () =>
      createColumns({
        ...props,
        onOpenDetails: (row) => onOpenDetailsRef.current(row),
        onPreviewSlice: props.onPreviewSlice
          ? (row) => onPreviewSliceRef.current?.(row)
          : undefined,
        onTogglePick: (row) => onTogglePickRef.current(row),
      }),
    // Handlers are read from refs so column defs stay stable across parent re-renders
    // (open previews were causing Rapid ReportDetail updates that remounted Preview buttons).
    [
      props.candleTimeframe,
      props.pickedSliceIds,
      props.previewActiveKey,
      props.previewLoadingKey,
      Boolean(props.onPreviewSlice),
    ],
  );

  useLayoutEffect(() => {
    if (!props.activeRowId || !props.evidencePulseNonce) return;
    if (props.evidencePulseNonce === lastHandledEvidencePulseRef.current) return;

    const rowIndex = props.rows.findIndex((row) => {
      const id = buildExplorerSliceId(row) || String(row.segment_key ?? "");
      return id === props.activeRowId;
    });
    if (rowIndex < 0) return;

    lastHandledEvidencePulseRef.current = props.evidencePulseNonce;
    return scheduleScrollMarketContextRow({
      containerAttr: "data-exit-placement-explorer-table",
      rowSelector: marketContextActiveRowSelector(),
      maxAttempts: 28,
      attemptMs: 75,
    });
  }, [props.activeRowId, props.evidencePulseNonce, props.rows]);

  return (
    <div
      data-exit-placement-explorer-table=""
      data-market-context-pivot-table=""
      data-market-context-pivot-table-embedded=""
      className="w-full min-w-0"
    >
      <div
        data-market-context-pivot-scroll=""
        className="overflow-hidden rounded-[6px] border border-border bg-background"
      >
        <div
          data-market-context-pivot-scroll-inner=""
          className="max-h-[calc(2.5rem+2.5rem*var(--market-context-pivot-visible-rows))] overflow-x-auto overflow-y-auto overscroll-y-contain [&_[data-slot=table-head]]:sticky [&_[data-slot=table-head]]:top-0 [&_[data-slot=table-head]]:z-[1] [&_[data-slot=table-head]]:bg-background [&_[data-slot=table-header]_[data-slot=table-row]]:shadow-[inset_0_-1px_0_0_var(--border)]"
          style={
            {
              ["--market-context-pivot-visible-rows" as string]: String(
                MARKET_CONTEXT_PIVOT_VISIBLE_ROW_COUNT,
              ),
            } as React.CSSProperties
          }
        >
          <DataTable
            columns={columns}
            data={props.rows}
            getRowId={(row) => buildExplorerSliceId(row) || String(row.segment_key ?? "")}
            emptyMessage={props.emptyMessage ?? "No contexts match these filters."}
            showPagination={false}
            showTableBorder={false}
            pagination={{ pageIndex: 0, pageSize: Math.max(props.rows.length, 1) }}
            activeRowId={props.activeRowId}
            evidencePulseNonce={props.evidencePulseNonce}
            getRowDataAttributes={(row) => {
              const id = buildExplorerSliceId(row) || String(row.segment_key ?? "");
              return props.evidencePulseNonce && id === props.activeRowId
                ? { "data-market-context-evidence-highlight": String(props.evidencePulseNonce) }
                : undefined;
            }}
            className="min-w-0"
          />
        </div>
      </div>
    </div>
  );
}
